import { Component, type PropsWithChildren } from "react";

import { isDataLoaderMismatchError } from "@shared/helpers/isDataLoaderMismatchError";

interface State {
  error: unknown;
}

/**
 * Renders nothing while `useSafeLoaderData` hard reloads the document, and hands every other
 * error to the enclosing boundary. Mount it inside a Sentry boundary: React stops propagation at
 * the innermost boundary, so the outer one neither renders a fallback nor reports an error that
 * resolves itself on the reload.
 */
class DataLoaderMismatchBoundary extends Component<PropsWithChildren, State> {
  constructor(props: PropsWithChildren) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  render() {
    const { error } = this.state;

    if (error === null) {
      return this.props.children;
    }

    // React has no API to decline an error once caught, so rethrowing during render is the only
    // way to let an unrelated failure reach the boundary above this one.
    if (!isDataLoaderMismatchError(error)) {
      throw error;
    }

    return null;
  }
}

export { DataLoaderMismatchBoundary };
